import React, { useCallback, useContext, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Button, Checkbox, Dialog, DialogContent, FormControlLabel, Typography } from "@mui/material";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import CustomConfirmationChangesDialog from "../../../../containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import PickExportSendBlocks from "../PickExportSendBlocks";
import { ProformaEmployeeCostReportContext } from "../../ProformaEmployeeCostReport/ProformaEmployeeCostReport";
import { useTranslation } from "react-i18next";

interface Props {
  preHandleExport: (
    paySchema: boolean,
    costSchema: boolean,
    sectionsWithZeroAmount: boolean,
    saveBefore: boolean,
    monthlyView: boolean
  ) => Promise<void>;
  allFieldsValidated: boolean;
}

interface State {
  open: boolean;
  paySchema: boolean;
  costSchema: boolean;
  sectionsWithZeroAmount: boolean;
  CCCDOpen: boolean;
  monthlyView: boolean;
}

const EmployeeCostExportAction = ({ preHandleExport, allFieldsValidated }: Props) => {
  const { viewMode } = useContext(ProformaEmployeeCostReportContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.export_expense_report";

  const [state, setState] = useState<State>({
    open: false,
    paySchema: true,
    costSchema: true,
    sectionsWithZeroAmount: true,
    CCCDOpen: false,
    monthlyView: false,
  });

  const handleUpdateExportedBlocks = useCallback(
    (field: "costSchema" | "paySchema" | "sectionsWithZeroAmount", value: boolean) => {
      console.log(field, value);
      setState((state) => ({
        ...state,
        [field]: value,
      }));
    },
    []
  );

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setState((state) => ({ ...state, open: true }))}
      >
        {`${t(`${tRP}.export`)}`}
      </Button>
      <Dialog open={state.open}>
        <DialogTitle
          title={`${t(`${tRP}.export_proforma_report`)}`}
          handleClose={() => setState((state) => ({ ...state, open: false }))}
        />
        <DialogContent>
          <PickExportSendBlocks
            handleUpdateParent={handleUpdateExportedBlocks}
            paySchema={state.paySchema}
            costSchema={state.costSchema}
            sectionsWithZeroAmount={state.sectionsWithZeroAmount}
          />
          <FormControlLabel
            label={
              <Typography>
                {`${t(`${tRP}.export_monthly_amount`)}`}
                <Typography variant="body2">({`${t(`${tRP}.all_amounts_will_be_divided_by`)}`})</Typography>
              </Typography>
            }
            control={
              <Checkbox
                checked={state.monthlyView}
                onChange={(_, checked) => setState((state) => ({ ...state, monthlyView: checked }))}
              />
            }
          />
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, open: false }))}
          handleSave={
            !viewMode
              ? () => setState((state) => ({ ...state, CCCDOpen: true, open: false }))
              : () => {
                  preHandleExport(
                    state.paySchema,
                    state.costSchema,
                    state.sectionsWithZeroAmount,
                    false,
                    state.monthlyView
                  );
                  setState((state) => ({ ...state, open: false }));
                }
          }
          customSaveName={`${t(`${tRP}.export`)}`}
        />
      </Dialog>
      <CustomConfirmationChangesDialog
        loading={false}
        open={state.CCCDOpen}
        title={`${t(`${tRP}.confirmation_dialog_title`)}`}
        children={
          <Typography>
            {allFieldsValidated ? `${t(`${tRP}.confirmation_message`)}` : `${t(`${tRP}.confirmation_warning`)}`}
          </Typography>
        }
        handleCancel={() => setState((state) => ({ ...state, CCCDOpen: false }))}
        actionsAndHandlers={
          allFieldsValidated
            ? [
                {
                  action: `${t(`${tRP}.no_need_to_save`)}`,
                  handler: () => {
                    preHandleExport(
                      state.paySchema,
                      state.costSchema,
                      state.sectionsWithZeroAmount,
                      false,
                      state.monthlyView
                    );
                    setState((state) => ({ ...state, CCCDOpen: false }));
                  },
                  customButtonStyle: {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  },
                },
                {
                  action: `${t(`${tRP}.save_before_exporting`)}`,
                  handler: () => {
                    preHandleExport(
                      state.paySchema,
                      state.costSchema,
                      state.sectionsWithZeroAmount,
                      true,
                      state.monthlyView
                    );
                    setState((state) => ({ ...state, CCCDOpen: false }));
                  },
                  customButtonStyle: {
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.primary.contrastText,
                  },
                },
              ]
            : [
                {
                  action: `${t(`${tRP}.export_anyway`)}`,
                  handler: () => {
                    preHandleExport(
                      state.paySchema,
                      state.costSchema,
                      state.sectionsWithZeroAmount,
                      false,
                      state.monthlyView
                    );
                    setState((state) => ({ ...state, CCCDOpen: false }));
                  },
                  customButtonStyle: {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  },
                },
              ]
        }
      />
    </>
  );
};

export default EmployeeCostExportAction;
